
import { useState, useEffect, useCallback, startTransition } from "react";
import { MessageType } from "@/components/chat/ChatMessage";
import { parseWebSocketMessage, createUserMessage, formatMessageForWebSocket } from "./websocketUtils";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";

interface UseMessageHandlingProps {
  socket: WebSocket | null;
  selectedAgentId: string;
}

export const useMessageHandling = ({ socket, selectedAgentId }: UseMessageHandlingProps) => {
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialMessageReceived, setInitialMessageReceived] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Set up message handler
  useEffect(() => {
    if (!socket) return;
    
    const handleMessage = (event: MessageEvent) => {
      console.log('Message received:', event.data);
      try {
        const data = JSON.parse(event.data);
        const result = parseWebSocketMessage(data, initialMessageReceived, messages);
        
        if (result.message) {
          if (result.isInitialMessage) {
            startTransition(() => {
              setMessages([result.message!]);
              setInitialMessageReceived(true);
            });
          } else {
            startTransition(() => {
              setMessages((prev) => [...prev, result.message!]);
              setIsLoading(false);
            });
          }
        }
        
        if (result.isError) {
          toast({
            title: "Error",
            description: result.errorMessage || "An error occurred",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error, event.data);
      }
    };
    
    socket.addEventListener('message', handleMessage);
    
    return () => {
      socket.removeEventListener('message', handleMessage);
    };
  }, [socket, initialMessageReceived, messages, toast]);

  const sendMessage = useCallback((text: string) => {
    if (!text.trim()) return;
    
    // Add user message to UI
    const userMessage = createUserMessage(text);
    startTransition(() => {
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
    });
    
    // Send message via WebSocket if connected
    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        // Use the enhanced structured format
        const formattedMessage = formatMessageForWebSocket(text, user, {
          useStructuredFormat: true,
          additionalMetadata: {
            agent_id: selectedAgentId,
            timestamp: new Date().toISOString()
          }
        });
        
        console.log('Sending structured message:', formattedMessage);
        socket.send(formattedMessage);
      } catch (error) {
        console.error('Error sending message:', error);
        startTransition(() => {
          setIsLoading(false);
        });
        
        toast({
          title: "Error",
          description: "Failed to send message",
          variant: "destructive",
        });
      }
    } else {
      startTransition(() => {
        setIsLoading(false);
      });
      
      toast({
        title: "Not Connected",
        description: "Chat server is not connected. Attempting to reconnect...",
        variant: "destructive",
      });
    }
  }, [socket, toast, user, selectedAgentId]);

  const resetMessages = useCallback(() => {
    startTransition(() => {
      setInitialMessageReceived(false);
    });
  }, []);

  return {
    messages,
    isLoading,
    sendMessage,
    resetMessages
  };
};
